import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Ticket } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, getErrorMessage } from "@/lib/api";

const STATUS = {
  pending_kyc: { label: "Waiting on KYC", tone: "text-warning" },
  active: { label: "Active", tone: "text-up" },
  expired: { label: "Expired", tone: "text-muted-foreground" },
  used: { label: "Used", tone: "text-muted-foreground" },
  revoked: { label: "Revoked", tone: "text-down" },
};

const ACTION = {
  granted: "Granted",
  pending_kyc: "Held for KYC",
  activated: "Unlocked",
  expired: "Expired",
  revoked: "Revoked",
  credited: "Credited",
};

function money(amount, currency) {
  const n = Number(amount || 0);
  const cur = (currency || "INR").toUpperCase();
  if (cur === "INR") {
    return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  }
  return `${n.toLocaleString("en-IN", { maximumFractionDigits: 8 })} ${cur}`;
}

function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function RewardsPage() {
  const [offers, setOffers] = useState(null);
  const [mine, setMine] = useState(null);
  const [history, setHistory] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback(() => {
    setLoadErr("");
    return Promise.all([
      api.get("/api/rewards/offers"),
      api.get("/api/rewards/my-vouchers"),
      api.get("/api/rewards/history", { params: { limit: 30 } }),
    ])
      .then(([o, m, h]) => {
        setOffers(o.data.items || []);
        setMine(m.data);
        setHistory(h.data.items || []);
      })
      .catch((e) => setLoadErr(getErrorMessage(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function redeem(raw) {
    const next = String(raw || code).trim();
    if (!next) return;
    setBusy(true);
    setMsg("");
    setErr("");
    try {
      const { data } = await api.post("/api/rewards/redeem", { code: next });
      const held = data.status === "pending_kyc";
      setMsg(
        held
          ? `${data.name} is reserved. Complete KYC to unlock ${money(data.reward_amount, data.reward_currency)}.`
          : `${money(data.reward_amount, data.reward_currency)} from ${data.name} is in your bonus wallet.`,
      );
      setCode("");
      await load();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const balances = mine?.bonus_balances || [];
  const vouchers = mine?.items || [];

  return (
    <AppShell>
      <div className="space-y-6" data-testid="rewards-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Rewards & bonuses</h1>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Offers set in the admin panel. Bonus credit can be used for trading and cannot be withdrawn.
            </p>
          </div>
        </div>

        {loadErr && <p className="text-sm text-down">{loadErr}</p>}

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <p className="text-xs text-muted-foreground">Bonus balance</p>
            {balances.length === 0 ? (
              <p className="num mt-2 text-lg font-semibold">₹0</p>
            ) : (
              <ul className="mt-2 space-y-1">
                {balances.map((b) => (
                  <li key={b.currency} className="num text-lg font-semibold">
                    {money(b.available, b.currency)}
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Spendable on trades only</p>
          </div>
          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <p className="text-xs text-muted-foreground">Active rewards</p>
            <p className="num mt-2 text-lg font-semibold">{mine?.active_vouchers ?? "—"}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <p className="text-xs text-muted-foreground">Waiting on KYC</p>
            <p className="num mt-2 text-lg font-semibold">{mine?.pending_kyc ?? "—"}</p>
            {(mine?.pending_kyc || 0) > 0 && (
              <Link to="/profile/kyc" className="mt-1 inline-block text-xs text-primary hover:underline">
                Finish verification
              </Link>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-surface/50 p-5">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <Ticket className="h-4 w-4 text-primary" /> Redeem a code
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Enter a promotional or manual code created in the admin panel.
          </p>
          <form
            className="mt-4 flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              redeem(code);
            }}
          >
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="PROMO CODE"
              className="h-10 min-w-[200px] flex-1 rounded-lg border border-border bg-surface-2 px-3 text-sm uppercase outline-none focus:border-primary"
              data-testid="reward-code-input"
            />
            <button
              type="submit"
              disabled={busy || !code.trim()}
              className="brand-gradient rounded-lg px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              data-testid="reward-redeem-btn"
            >
              {busy ? "Claiming…" : "Claim"}
            </button>
          </form>
          {msg && <p className="mt-3 text-sm text-up">{msg}</p>}
          {err && <p className="mt-3 text-sm text-down">{err}</p>}
        </div>

        <section>
          <h2 className="text-lg font-semibold">Available offers</h2>
          <p className="mt-1 text-xs text-muted-foreground">Live vouchers from the admin rewards console.</p>
          {offers === null ? (
            <div className="skeleton mt-3 h-24" />
          ) : offers.length === 0 ? (
            <p className="mt-3 rounded-xl border border-border bg-surface/40 px-4 py-8 text-center text-sm text-muted-foreground">
              No promotional rewards are live right now. Signup and KYC bonuses still apply automatically when an admin enables them.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {offers.map((o) => (
                <article key={o.voucher_id} className="rounded-xl border border-border bg-surface/50 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold">{o.name}</h3>
                      {o.description && <p className="mt-1 text-xs text-muted-foreground">{o.description}</p>}
                    </div>
                    <Gift className="h-4 w-4 shrink-0 text-primary" />
                  </div>
                  <p className="num mt-3 text-lg font-semibold">{money(o.reward_amount, o.reward_currency)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {o.reward_type === "fee_discount" ? "Fee discount" : "Bonus credit"}
                    {o.validity_days ? ` · ${o.validity_days} days` : ""}
                    {o.requires_kyc ? " · KYC required" : ""}
                  </p>
                  <p className="mt-2 font-mono text-xs tracking-wide text-primary">{o.code}</p>
                  <button
                    type="button"
                    disabled={busy || o.claimed}
                    onClick={() => redeem(o.code)}
                    className="mt-4 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/50 disabled:opacity-50"
                  >
                    {o.claimed ? "Claimed" : "Claim this offer"}
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold">Your rewards</h2>
          {mine === null ? (
            <div className="skeleton mt-3 h-24" />
          ) : vouchers.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">Nothing granted yet.</p>
          ) : (
            <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-surface/40">
              <div className="min-w-[560px]">
                <div className="table-head grid grid-cols-[1.4fr_1fr_0.8fr_1fr] gap-3 px-4 py-2.5">
                  <span>Reward</span>
                  <span className="text-right">Amount</span>
                  <span className="text-right">Status</span>
                  <span className="text-right">Expires</span>
                </div>
                {vouchers.map((v) => {
                  const st = STATUS[v.status] || { label: v.status, tone: "text-muted-foreground" };
                  return (
                    <div key={v.user_voucher_id} className="terminal-row grid-cols-[1.4fr_1fr_0.8fr_1fr] gap-3 px-4 py-3 text-sm">
                      <span>
                        <span className="block">{v.voucher_name || "Reward"}</span>
                        <span className="font-mono text-xs text-muted-foreground">{v.voucher_code}</span>
                      </span>
                      <span className="num text-right">{money(v.reward_amount, v.reward_currency)}</span>
                      <span className={`text-right text-xs ${st.tone}`}>{st.label}</span>
                      <span className="num text-right text-xs text-muted-foreground">{when(v.expires_at)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        <section>
          <h2 className="text-lg font-semibold">History</h2>
          {history === null ? (
            <div className="skeleton mt-3 h-20" />
          ) : history.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No reward activity yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-border rounded-xl border border-border bg-surface/40">
              {history.map((row) => (
                <li key={row.reward_tx_id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                  <span>
                    <span className="block">{ACTION[row.action] || row.action}</span>
                    <span className="text-xs text-muted-foreground">{when(row.created_at)}</span>
                  </span>
                  <span className="num">{money(row.amount, row.currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
